import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  PROVIDER_SOURCE_OPTIONS,
  TEST_DESCRIPTION_MAX,
  testSetupErrors,
  type ProviderDraft,
  type TestSetupDraft,
} from '@/lib/lis/orderingProvider'

/** Radix refuses an empty-string item value, so "no provider" needs a sentinel. */
const NO_SOURCE = '__none__'

/**
 * Add or edit one stage test: its code, its description (sent to Epic as the
 * test name) and where its ordering provider comes from (z8nrz7ffvz).
 *
 * Errors show beside the field once the form has been submitted, and Save
 * stays available so pressing it explains what is missing.
 */
export function LisStageTestForm({
  initial,
  codeLocked,
  submitLabel,
  pending,
  serverError,
  onSubmit,
  onCancel,
}: {
  initial: TestSetupDraft
  /** Editing: the code is the test's identity and is not renamed here. */
  codeLocked?: boolean
  submitLabel: string
  pending: boolean
  serverError?: string | null
  onSubmit: (draft: TestSetupDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  const [submitted, setSubmitted] = useState(false)
  const errors = submitted ? testSetupErrors(draft) : {}

  const set = <K extends keyof TestSetupDraft>(key: K, value: TestSetupDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))
  const setProvider = (key: keyof ProviderDraft, value: string) =>
    setDraft((d) => ({ ...d, provider: { ...d.provider, [key]: value } }))

  const source = PROVIDER_SOURCE_OPTIONS.find((o) => o.value === draft.providerSource)

  return (
    <form
      className='space-y-3 rounded-lg border bg-muted/20 p-3'
      onSubmit={(e) => {
        e.preventDefault()
        setSubmitted(true)
        if (Object.keys(testSetupErrors(draft)).length === 0) onSubmit(draft)
      }}
    >
      <p className='text-xs text-muted-foreground'>
        Code and Description go to Epic as the test (OBR-4, <span className='font-mono'>code^description</span>).
      </p>

      <div className='grid gap-3 sm:grid-cols-[160px_1fr]'>
        <FormField id='lis-test-code' label='Code' error={errors.testCode}>
          <Input
            id='lis-test-code'
            value={draft.testCode}
            onChange={(e) => set('testCode', e.target.value)}
            placeholder='e.g. CTCultureBACT'
            className='font-mono'
            disabled={codeLocked}
          />
        </FormField>
        <FormField id='lis-test-desc' label='Description (sent to Epic as the test name)' error={errors.description}>
          <Input
            id='lis-test-desc'
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder='e.g. CT Culture BACT'
            maxLength={TEST_DESCRIPTION_MAX}
          />
        </FormField>
      </div>

      <FormField id='lis-test-source' label='Ordering provider source'>
        <Select
          value={draft.providerSource || NO_SOURCE}
          onValueChange={(v) => set('providerSource', v === NO_SOURCE ? '' : (v as TestSetupDraft['providerSource']))}
        >
          <SelectTrigger id='lis-test-source'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_SOURCE}>No ordering provider</SelectItem>
            {PROVIDER_SOURCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-[11px] text-muted-foreground'>
          {source?.hint ?? 'Orders of this test are sent without an ordering provider (ORC-12 / OBR-16 empty).'}
        </p>
      </FormField>

      {draft.providerSource ? (
        <>
          <div className='space-y-1.5'>
            <p className='text-xs font-medium'>
              {draft.providerSource === 'fixed' ? 'Provider' : 'Default provider (optional)'}
            </p>
            <div className='grid gap-2 sm:grid-cols-2'>
              <Input
                aria-label='NPI'
                value={draft.provider.npi}
                onChange={(e) => setProvider('npi', e.target.value)}
                placeholder={draft.providerSource === 'fixed' ? 'NPI (10 digits)' : 'NPI (10 digits, optional)'}
                inputMode='numeric'
                className='font-mono'
              />
              <Input
                aria-label='Last name'
                value={draft.provider.lastName}
                onChange={(e) => setProvider('lastName', e.target.value)}
                placeholder='Last name (optional)'
              />
              <Input
                aria-label='First name'
                value={draft.provider.firstName}
                onChange={(e) => setProvider('firstName', e.target.value)}
                placeholder='First name (optional)'
              />
              <Input
                aria-label='Middle name'
                value={draft.provider.middleName}
                onChange={(e) => setProvider('middleName', e.target.value)}
                placeholder='Middle name (optional)'
              />
            </div>
            {errors.provider ? <p className='text-xs text-destructive'>{errors.provider}</p> : null}
          </div>

          <label className='flex items-start gap-2 text-sm'>
            <input
              type='checkbox'
              className='mt-1'
              checked={draft.blockIfNoProvider}
              onChange={(e) => set('blockIfNoProvider', e.target.checked)}
            />
            <span>
              Block the order if no provider is found
              <span className='block text-[11px] text-muted-foreground'>
                Off: the order is placed without a provider and the screen says so. On: the order is not
                placed until a provider is found.
              </span>
            </span>
          </label>
        </>
      ) : null}

      <label className='flex items-center gap-2 text-sm'>
        <input
          type='checkbox'
          checked={draft.defaultSelected}
          onChange={(e) => set('defaultSelected', e.target.checked)}
        />
        Pre-selected when the stage is chosen
      </label>

      {serverError ? <p className='text-sm text-destructive'>{serverError}</p> : null}

      <div className='flex justify-end gap-2'>
        <Button type='button' size='sm' variant='outline' onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type='submit' size='sm' disabled={pending}>
          {pending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

function FormField({
  id,
  label,
  error,
  children,
}: {
  id: string
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className='space-y-1.5'>
      <Label htmlFor={id} className='text-xs'>
        {label}
      </Label>
      {children}
      {error ? <p className='text-xs text-destructive'>{error}</p> : null}
    </div>
  )
}
